import { Inject, Injectable } from '@angular/core';
import {
  HttpClientService,
  TOKENS as COMMON_TOKENS,
  ExportToken,
  LogService,
} from '@trustedshops/tswp-core-common';
import {
  AccountWorld,
  AccountWorldValue,
  ChannelFeatureLevel,
  ChannelMatchMode,
  ChannelMatchModeValue,
  FeatureMatchMode,
  HasFeatureParams,
  ProductFeatureConfig,
  ProductFeatureData,
  ProductFeatureService,
  RawProductFeatureData,
  TOKENS as MASTERDATA_TOKENS,
} from '@trustedshops/tswp-core-masterdata';
import { toPromise } from '@trustedshops/tswp-core-common-eventing-rxjs';

/**
 * Contract tiers returned by the V2 companies API (`contractType`). Internal to this service —
 * only the derived `accountWorld` is exposed on the public payload. `LEGACY` marks Old World /
 * migrated / non-BENELUX accounts; every other value marks a New World contract.
 */
type ContractType =
  | 'NONE'
  | 'BASIC_REGISTRATION'
  | 'BASIC_VERIFIED'
  | 'PLUS_STARTER'
  | 'PLUS_GROW'
  | 'PLUS_PROFESSIONAL'
  | 'PLUS_ENTERPRISE'
  | 'THIRD_PARTY'
  | 'LEGACY';

/** Minimal slice of the V2 `GET /companies/{id}` response this service reads. */
interface CompanyV2Response {
  contractType?: ContractType;
}

@Injectable()
@ExportToken(MASTERDATA_TOKENS.ProductFeatureService)
export class ProductFeatureServiceImpl implements ProductFeatureService {
  private static readonly TYPE = 'ProductFeatureService';

  private cachedFeaturesPayload: ProductFeatureData | null = null;
  // Account-wide intersection — a feature is present only when it exists on every channel. Used for
  // an account-wide check with the default `channelMatch: ChannelMatchMode.All`.
  private cachedAccountWideFeatureSet: Set<string> | null = null;
  // Account-wide union — a feature is present when booked on any channel. Used for an account-wide
  // check with `channelMatch: ChannelMatchMode.Some`.
  private cachedChannelUnionFeatureSet: Set<string> | null = null;
  // Per-channel feature-name sets, keyed by channelId.
  private cachedChannelFeatureSets: Map<string, Set<string>> | null = null;
  // accountLevel feature names — the fallback when a feature is not found in channelLevel.
  private cachedAccountFeatureSet: Set<string> | null = null;
  // Default upper bound for the non-fatal company fetch. It only patches
  // accountWorld, so a slow or stalled response must never hold getFeatures()
  // open. A carrier can raise it per stage via
  // ProductFeatureConfig.companyFetchTimeoutMs.
  public static readonly DEFAULT_COMPANY_FETCH_TIMEOUT_MS = 10_000;
  private companyFetchTimeoutMs: number =
    ProductFeatureServiceImpl.DEFAULT_COMPANY_FETCH_TIMEOUT_MS;
  private readonly loadingOperation: Promise<void>;
  // Resolves when the non-fatal company fetch has landed and accountWorld is
  // patched into the cached payload. getFeatures() awaits it; hasFeature()
  // does not, because accountWorld does not affect feature resolution.
  private _companyLoadedOperation: Promise<void> = Promise.resolve();
  private _resolveInitialization: (value: void | PromiseLike<void>) => void;
  private _rejectInitialization: (reason?: unknown) => void;

  public constructor(
    @Inject(COMMON_TOKENS.HttpClientService)
    private readonly _httpClient: HttpClientService,
    @Inject(COMMON_TOKENS.LogService)
    private readonly _logService: LogService
  ) {
    this.loadingOperation = new Promise<void>((resolve, reject) => {
      this._resolveInitialization = resolve;
      this._rejectInitialization = reject;
    });
  }

  /**
   * Fetches the PFM payload once and caches it for the lifetime of the session.
   *
   * Initialization sequence:
   * 1. In parallel: GET /accounts/{id}/features (features) and GET /v2/companies/{id} (contract).
   *    The features fetch is fatal — a failure rejects initialize(). The company fetch is
   *    NON-FATAL: any failure yields `accountWorld = UNKNOWN` (see `fetchCompanySafe`). The
   *    company fetch is also NON-BLOCKING: `initialize()` and `hasFeature()` resolve after the
   *    features fetch alone, and the company response patches `accountWorld` afterwards. Only
   *    `getFeatures()` waits for the patch.
   * 2. `accountWorld` is derived from the company's `contractType` (`LEGACY` → `Legacy`, any other
   *    value → `NewWorld`, or `Unknown` when the company response or `contractType` is missing).
   *    It is metadata only and does NOT affect feature resolution.
   * 3. `hasFeature()` resolves a feature from `channelLevel` first, then falls back to
   *    `accountLevel`. `computeChannelIntersection()` stores the account-wide channel intersection
   *    (default) and `computeChannelUnion()` the account-wide union (used when
   *    `channelMatch: ChannelMatchMode.Some`); `buildChannelFeatureSets()` the per-channel sets, and
   *    `cachedAccountFeatureSet` the accountLevel names. The API payload is never mutated —
   *    `getFeatures()` returns the normalised server response (plus `accountWorld`).
   */
  public async initialize(config: ProductFeatureConfig): Promise<void> {
    this._logService.trace(
      ProductFeatureServiceImpl.TYPE,
      'Initializing ProductFeatureService...'
    );

    this.companyFetchTimeoutMs =
      config.companyFetchTimeoutMs ??
      ProductFeatureServiceImpl.DEFAULT_COMPANY_FETCH_TIMEOUT_MS;

    try {
      this._logService.trace(
        ProductFeatureServiceImpl.TYPE,
        'Fetching features and company...'
      );
      const featuresUrl = `${config.basePath}/accounts/${config.accountId}/features`;
      // Start both fetches at once. The features fetch is fatal and gates
      // hasFeature(); the company fetch is non-fatal (fetchCompanySafe never
      // rejects) and only patches accountWorld later — see below. hasFeature()
      // must NOT wait for the company response: that wait blocked the plugin
      // fan-out for the full company TTFB.
      const companyUrl = `${config.companiesBasePath}/v2/companies/${config.accountId}`;
      const featuresPromise = toPromise<RawProductFeatureData | null>(
        this._httpClient.get<RawProductFeatureData | null>(featuresUrl, {})
      );
      const companyPromise = this.fetchCompanySafe(companyUrl);

      const featuresPayload = await featuresPromise;

      this._logService.trace(
        ProductFeatureServiceImpl.TYPE,
        'Features fetched',
        featuresPayload
      );
      if (!featuresPayload) {
        this.cachedFeaturesPayload = null;
        this.cachedAccountWideFeatureSet = null;
        this.cachedChannelUnionFeatureSet = null;
        this.cachedChannelFeatureSets = null;
        this.cachedAccountFeatureSet = null;
      } else {
        // Build the caches with accountWorld UNKNOWN. accountWorld does not
        // affect feature resolution, so hasFeature() unblocks here.
        const normalized = this.normalize(
          featuresPayload,
          AccountWorld.Unknown
        );
        this.cachedFeaturesPayload = normalized;
        this.cachedAccountWideFeatureSet = this.computeChannelIntersection(
          normalized.channelLevel
        );
        this.cachedChannelUnionFeatureSet = this.computeChannelUnion(
          normalized.channelLevel
        );
        this.cachedChannelFeatureSets = this.buildChannelFeatureSets(
          normalized.channelLevel
        );
        this.cachedAccountFeatureSet = new Set(
          normalized.accountLevel.features.map((f) => f.name)
        );
      }

      // Patch accountWorld when the company response lands. getFeatures()
      // awaits this patch, so accountWorld consumers always see the final
      // value. The assignment happens before _resolveInitialization() so a
      // pending getFeatures() call always awaits the patch.
      this._companyLoadedOperation = companyPromise.then((company) => {
        if (this.cachedFeaturesPayload) {
          this.cachedFeaturesPayload = {
            ...this.cachedFeaturesPayload,
            accountWorld: this.deriveAccountWorld(company),
          };
        }
      });

      this._resolveInitialization();
      this._logService.trace(
        ProductFeatureServiceImpl.TYPE,
        'Finished initialization'
      );
    } catch (error) {
      this._rejectInitialization(error);
      this._logService.error(ProductFeatureServiceImpl.TYPE, error as Error);
      throw error;
    }
  }

  /**
   * Returns the normalised cached payload including the service-derived `accountWorld`.
   * A feature may live in either `accountLevel` or `channelLevel`; use `hasFeature()` to check
   * entitlements rather than reading the payload directly.
   */
  public async getFeatures(): Promise<ProductFeatureData | null> {
    await this.loadingOperation;
    // Wait for the accountWorld patch so consumers never see a transient
    // UNKNOWN while the company fetch is still in flight.
    await this._companyLoadedOperation;
    return this.cachedFeaturesPayload;
  }

  /**
   * Checks whether the account has the requested feature(s). Each feature is resolved from
   * `channelLevel` first, then falls back to `accountLevel`; `accountWorld` does NOT affect the
   * result.
   *
   * - `channelId` given → that channel's feature set; otherwise the channels are combined per
   *   `channelMatch`: `ChannelMatchMode.All` (default) requires every channel (intersection),
   *   `ChannelMatchMode.Some` accepts any channel (union).
   * - If the feature is not found in `channelLevel`, it is looked up in `accountLevel`.
   *
   * `mode` combines multiple `featureNames` — each resolved independently:
   * - `FeatureMatchMode.All` (default) → every name must be present (AND)
   * - `FeatureMatchMode.Some` → at least one name must be present (OR)
   *
   * `mode` (feature names) and `channelMatch` (channels) are independent axes. An empty
   * `featureNames` array always returns `false` regardless of mode.
   */
  public async hasFeature(params: HasFeatureParams): Promise<boolean> {
    await this.loadingOperation;
    if (!this.cachedFeaturesPayload) return false;

    const featureNames = Array.isArray(params.featureNames)
      ? (params.featureNames as readonly string[])
      : [params.featureNames as string];

    if (featureNames.length === 0) return false;

    const requireAll =
      (params.mode ?? FeatureMatchMode.All) === FeatureMatchMode.All;
    const channelMatch = params.channelMatch ?? ChannelMatchMode.All;

    const present = (name: string): boolean =>
      this.isFeaturePresent(name, params.channelId, channelMatch);

    return requireAll
      ? featureNames.every(present)
      : featureNames.some(present);
  }

  /**
   * Resolves a single feature: search `channelLevel` first, then fall back to `accountLevel`. With
   * a `channelId`, that channel's set is used; otherwise the account-wide set selected by
   * `channelMatch` (union for `Any`, intersection for `All` — the default).
   */
  private isFeaturePresent(
    featureName: string,
    channelId?: string,
    channelMatch: ChannelMatchModeValue = ChannelMatchMode.All
  ): boolean {
    const channelSets = this.cachedChannelFeatureSets;
    const accountWideSet =
      channelMatch === ChannelMatchMode.Some
        ? this.cachedChannelUnionFeatureSet
        : this.cachedAccountWideFeatureSet;
    const channelHit =
      channelId && channelSets && channelSets.size > 0
        ? channelSets.get(channelId)?.has(featureName) ?? false
        : accountWideSet?.has(featureName) ?? false;

    if (channelHit) return true;

    return this.cachedAccountFeatureSet?.has(featureName) ?? false;
  }

  /**
   * Fetches the V2 company response without ever rejecting. The company contract is non-fatal:
   * any failure (synchronous throw from the HTTP client or async rejection) is logged and resolved
   * to `null`, leaving `accountWorld` UNKNOWN.
   */
  private async fetchCompanySafe(
    url: string
  ): Promise<CompanyV2Response | null> {
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    try {
      const request = toPromise<CompanyV2Response | null>(
        this._httpClient.get<CompanyV2Response | null>(url, {})
      );
      // A request that never settles is not a rejection, so the catch below
      // cannot cover it. getFeatures() awaits this promise, so a hang would
      // leave every consumer loading forever with no error to surface.
      // Promise.race attaches a handler to `request`, so a late rejection
      // after the timeout wins stays handled.
      const timeout = new Promise<null>((resolve) => {
        timeoutHandle = setTimeout(() => {
          // error, not warning: the Instana logging target is configured with
          // [Error, Fatal] in qa and production, and no console target is
          // registered there, so a warning would reach nothing in exactly the
          // environments where this bound matters.
          this._logService.error(
            ProductFeatureServiceImpl.TYPE,
            new Error(
              `Company fetch did not settle within ${this.companyFetchTimeoutMs} ms; accountWorld stays UNKNOWN`
            )
          );
          resolve(null);
        }, this.companyFetchTimeoutMs);
      });
      return await Promise.race([request, timeout]);
    } catch (error) {
      this._logService.warning(
        ProductFeatureServiceImpl.TYPE,
        'Company fetch failed; accountWorld will be UNKNOWN',
        error
      );
      return null;
    } finally {
      clearTimeout(timeoutHandle);
    }
  }

  /**
   * Derives the account world from the company's `contractType`: `LEGACY` → `Legacy`, any other
   * value → `NewWorld`. Returns `Unknown` when the company payload or `contractType` is missing —
   * the account type is unknown, but this is metadata only and does not affect feature resolution,
   * so it is not treated as an error.
   */
  private deriveAccountWorld(
    company: CompanyV2Response | null
  ): AccountWorldValue {
    const contractType = company?.contractType;
    if (contractType == null) return AccountWorld.Unknown;
    return contractType === 'LEGACY'
      ? AccountWorld.Legacy
      : AccountWorld.NewWorld;
  }

  /**
   * Union of feature names across all channels — a feature is account-wide when it is booked on ANY
   * channel. Backs an account-wide check with `channelMatch: ChannelMatchMode.Some`. Returns an empty
   * set when there are no channels. The API payload is never mutated.
   */
  private computeChannelUnion(channels: ChannelFeatureLevel[]): Set<string> {
    return new Set(channels.flatMap((ch) => ch.features.map((f) => f.name)));
  }

  /**
   * Intersection of feature names across all channels — a feature is account-wide only when it is
   * present in every channel. Backs an account-wide check with the default
   * `channelMatch: ChannelMatchMode.All`; mirrors the legacy
   * `FeatureBookingService.hasAccountFeatureBooked()` rule. Returns an empty set when there are no
   * channels. The API payload is never mutated.
   */
  private computeChannelIntersection(
    channels: ChannelFeatureLevel[]
  ): Set<string> {
    if (channels.length === 0) return new Set();

    const allFeatureNames = [
      ...new Set(channels.flatMap((ch) => ch.features.map((f) => f.name))),
    ];
    const result = new Set<string>();

    for (const name of allFeatureNames) {
      if (channels.every((ch) => ch.features.some((f) => f.name === name))) {
        result.add(name);
      }
    }

    return result;
  }

  private buildChannelFeatureSets(
    channels: ChannelFeatureLevel[]
  ): Map<string, Set<string>> {
    const map = new Map<string, Set<string>>();
    for (const ch of channels) {
      map.set(ch.channelId, new Set(ch.features.map((f) => f.name)));
    }
    return map;
  }

  /**
   * Normalises the raw API payload into a fully-guaranteed `ProductFeatureData` shape and attaches
   * the service-derived `accountWorld`. Missing `accountLevel` / `channelLevel` are defaulted here
   * so that every value stored in `cachedFeaturesPayload` satisfies the required-field contract.
   */
  private normalize(
    featuresPayload: RawProductFeatureData,
    accountWorld: AccountWorldValue
  ): ProductFeatureData {
    return {
      accountLevel: { features: featuresPayload.accountLevel?.features ?? [] },
      channelLevel: featuresPayload.channelLevel ?? [],
      accountWorld,
    };
  }
}
